import {
  persistentReducedStream,
  reducer,
  actionCreator,
} from '@ardoq/rxbeach';
import type { GroupFoldState } from '@ardoq/graph-view';
import { AssistantDiagramRecipe } from './recipe';

export type AssistantDiagramState = {
  recipe: AssistantDiagramRecipe | null;
  error: string | null;
  /** Live group-folding summary reported by the diagram, driving the collapse/expand-all buttons. */
  foldState: GroupFoldState;
};

/**
 * Sets the current visualization recipe. Dispatched when the AI Assistant's
 * `generateDiagram` result is applied (see `executeGenerateDiagram` in
 * `aiControlledCommands/viewpoint/diagram/command.ts`). The recipe is applied to
 * the in-view graph by `applyRecipe`; each dispatch replaces it wholesale.
 */
export const setAssistantDiagramRecipe =
  actionCreator<AssistantDiagramRecipe | null>('[assistantDiagram] set recipe');

/** A received recipe could not be parsed/applied. */
export const assistantDiagramFailed = actionCreator<string>(
  '[assistantDiagram] generate failed'
);

/**
 * Fired by the "Optimize layout" view-pane action. A UI trigger, not state (no reducer):
 * the visualization subscribes to it to re-run its layout from scratch. Keeps the view-pane
 * registration decoupled from the diagram component.
 */
export const optimizeAssistantDiagramLayout = actionCreator(
  '[assistantDiagram] optimize layout'
);

/** Fired by the "Zoom in" / "Zoom out" view-pane actions. UI triggers (no reducer). */
export const assistantDiagramZoomIn = actionCreator(
  '[assistantDiagram] zoom in'
);
export const assistantDiagramZoomOut = actionCreator(
  '[assistantDiagram] zoom out'
);

/** Fired by the "Collapse all" / "Expand all" view-pane actions. UI triggers (no reducer). */
export const collapseAllAssistantDiagramGroups = actionCreator(
  '[assistantDiagram] collapse all groups'
);
export const expandAllAssistantDiagramGroups = actionCreator(
  '[assistantDiagram] expand all groups'
);

/** Reports the diagram's live group-folding summary (from graph-view's `onFoldStateChange`). */
export const assistantDiagramFoldStateChanged = actionCreator<GroupFoldState>(
  '[assistantDiagram] fold state changed'
);

const EMPTY_FOLD_STATE: GroupFoldState = { groupCount: 0, collapsedCount: 0 };

const defaultState: AssistantDiagramState = {
  recipe: null,
  error: null,
  foldState: EMPTY_FOLD_STATE,
};

const setRecipe = (
  state: AssistantDiagramState,
  recipe: AssistantDiagramRecipe | null
): AssistantDiagramState => ({
  ...state,
  recipe,
  error: null,
  foldState: EMPTY_FOLD_STATE,
});

const setError = (
  state: AssistantDiagramState,
  error: string
): AssistantDiagramState => ({ ...state, error });

const setFoldState = (
  state: AssistantDiagramState,
  foldState: GroupFoldState
): AssistantDiagramState => ({ ...state, foldState });

/**
 * Persistent so the recipe survives view remounts and navigation — it is produced
 * out-of-band (the AI Assistant) from the view's lifecycle.
 */
export const assistantDiagram$ = persistentReducedStream(
  'assistantDiagram$',
  defaultState,
  [
    reducer(setAssistantDiagramRecipe, setRecipe),
    reducer(assistantDiagramFailed, setError),
    reducer(assistantDiagramFoldStateChanged, setFoldState),
  ]
);
